import { z } from 'zod';

/**
 * Frontend form validation schemas with user-friendly error messages.
 *
 * NOTE: These schemas are intentionally different from @paction-2.0/util validation schemas:
 * - UI schemas: User-friendly messages, no normalization (preserve user input for display)
 * - Util schemas: Backend validation with normalization (trim + lowercase)
 *
 * Use UI validators for form validation, util validators for API/data layer.
 */

export const emailSchema = z
  .string()
  .email({ message: 'Enter a valid email address.' });
export const phoneSchema = z
  .string()
  .min(7, { message: 'Phone number is too short.' })
  .max(20, { message: 'Phone number is too long.' })
  .regex(/^[+\d().\-\s]+$/, { message: 'Enter a valid phone number.' });
export const postalCodeSchema = z
  .string()
  .regex(/^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/, {
    message: 'Enter a valid postal code.'
  });
export const currencySchema = z
  .number()
  .nonnegative({ message: 'Amount must be positive.' });
export const urlSchema = z.string().url({ message: 'Enter a valid URL.' });
export const passwordSchema = z
  .string()
  .min(8, { message: 'Use at least 8 characters.' })
  .regex(/[A-Z]/, { message: 'Add an uppercase letter.' })
  .regex(/[a-z]/, { message: 'Add a lowercase letter.' })
  .regex(/\d/, { message: 'Add a number.' });
export const fileSchema = z
  .object({ size: z.number(), type: z.string(), name: z.string() })
  .refine((file) => file.size < 15 * 1024 * 1024, {
    message: 'File must be under 15MB.'
  });

export type ValidationResult = string | null;

export function buildValidator<T>(schema: z.ZodType<T>) {
  return (value: unknown): ValidationResult => {
    const result = schema.safeParse(value);
    if (result.success) return null;
    return result.error.errors[0]?.message ?? 'Invalid value';
  };
}

export const validateEmail = buildValidator(emailSchema);
export const validatePhone = buildValidator(phoneSchema);
export const validatePostalCode = buildValidator(postalCodeSchema);
export const validateCurrency = (value: unknown) => {
  if (typeof value === 'string' && value.trim() === '')
    return 'Enter an amount';
  const num = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(num)) return 'Enter a valid amount';
  return buildValidator(currencySchema)(num);
};
export const validateUrl = buildValidator(urlSchema);
export const validatePassword = buildValidator(passwordSchema);
export const validateFile = buildValidator(fileSchema);
